import { useEffect, useState, useCallback } from 'react'
import { useQuery } from '@apollo/client'
import { useUsersMeQuery } from '@/gql/systemApi'
import { SignInQueryResult } from '@/apollo/AuthRoute'
import { IS_LOGGED_IN } from '@/apollo/queries'
import {
  NotificationCenterService,
  NotificationEventHandler,
  NotificationReadHandler,
  UnreadCountHandler,
} from '@/services/NotificationCenter/NotificationCenterService'

export interface UseNotificationCenterReturn {
  service: NotificationCenterService | null
  isReady: boolean
  onNotification: (handler: NotificationEventHandler) => () => void
  onNotificationRead: (handler: NotificationReadHandler) => () => void
  onUnreadCountUpdate: (handler: UnreadCountHandler) => () => void
}

/**
 * React hook for real-time notification management via Pusher.
 * Automatically creates and manages a NotificationCenterService instance for the current user,
 * providing methods to register event handlers for notifications, read status updates, and unread count changes.
 *
 * @returns {UseNotificationCenterReturn} Object containing:
 *   - `service`: NotificationCenterService instance (null when user not loaded)
 *   - `isReady`: Boolean indicating if the service is ready to receive notifications
 *   - `onNotification`: Function to register handlers for new notifications
 *   - `onNotificationRead`: Function to register handlers for read status updates
 *   - `onUnreadCountUpdate`: Function to register handlers for unread count changes
 *
 * @example
 * // Basic usage
 * const { service, isReady, onNotification } = useNotificationCenter()
 *
 * useEffect(() => {
 *   if (!service) return
 *
 *   const unsubscribe = onNotification((notification) => {
 *     console.log('New notification:', notification.title)
 *     // Update UI, show toast, etc.
 *   })
 *
 *   return unsubscribe
 * }, [service, onNotification])
 *
 * @example
 * // Complete event handling
 * const {
 *   service,
 *   isReady,
 *   onNotification,
 *   onNotificationRead,
 *   onUnreadCountUpdate
 * } = useNotificationCenter()
 *
 * useEffect(() => {
 *   if (!service) return
 *
 *   const unsubscribers = [
 *     onNotification((notification) => {
 *       addNotificationToUI(notification)
 *     }),
 *     onNotificationRead(({ id }) => {
 *       markNotificationAsRead(id)
 *     }),
 *     onUnreadCountUpdate(({ unread_count }) => {
 *       updateBadgeCount(unread_count)
 *     })
 *   ]
 *
 *   return () => unsubscribers.forEach(unsub => unsub())
 * }, [service, onNotification, onNotificationRead, onUnreadCountUpdate])
 *
 * @example
 * // Component with notification handling
 * function NotificationBell() {
 *   const { isReady, onUnreadCountUpdate } = useNotificationCenter()
 *   const [unreadCount, setUnreadCount] = useState(0)
 *
 *   useEffect(() => {
 *     const unsubscribe = onUnreadCountUpdate(({ unread_count }) => {
 *       setUnreadCount(unread_count)
 *     })
 *     return unsubscribe
 *   }, [onUnreadCountUpdate])
 *
 *   return (
 *     <button disabled={!isReady}>
 *       🔔 {unreadCount > 0 && <span>{unreadCount}</span>}
 *     </button>
 *   )
 * }
 */
export const useNotificationCenter = (): UseNotificationCenterReturn => {
  // Check if user is logged in before attempting to fetch user data
  const { data } = useQuery<SignInQueryResult>(IS_LOGGED_IN)
  const isLoggedIn = Boolean(data?.isLoggedIn)
  const { data: userMe, loading } = useUsersMeQuery({
    skip: !isLoggedIn,
  })
  const userId = userMe?.users_me?.id

  const [service, setService] = useState<NotificationCenterService | null>(null)
  const [isServiceReady, setIsServiceReady] = useState<boolean>(false)

  useEffect(() => {
    // Only create service if user is logged in and we have a userId
    if (isLoggedIn && userId) {
      const newService = new NotificationCenterService(userId)
      setService(newService)
      setIsServiceReady(newService.isReady)

      return () => {
        newService.disconnect()
      }
    }

    // Clean up service when user logs out
    setService(null)
    setIsServiceReady(false)
    return undefined
  }, [isLoggedIn, userId])

  // Handler registration methods
  const onNotification = useCallback(
    (handler: NotificationEventHandler) => {
      if (service) {
        return service.onNotification(handler)
      }

      // Return no-op unsubscribe function when service is null
      return () => {}
    },
    [service]
  )

  const onNotificationRead = useCallback(
    (handler: NotificationReadHandler) => {
      if (service) {
        return service.onNotificationRead(handler)
      }

      // Return no-op unsubscribe function when service is null
      return () => {}
    },
    [service]
  )

  const onUnreadCountUpdate = useCallback(
    (handler: UnreadCountHandler) => {
      if (service) {
        return service.onUnreadCountUpdate(handler)
      }

      // Return no-op unsubscribe function when service is null
      return () => {}
    },
    [service]
  )

  // Subscribe to ready state changes
  useEffect(() => {
    if (!service) return () => {}

    const handleReady = () => setIsServiceReady(true)
    const unsubscribe = service.onReady(handleReady)

    // Check initial state
    if (service.isReady) {
      handleReady()
    }

    return unsubscribe
  }, [service])

  // Return null service when not logged in, no userId, or still loading
  if (!isLoggedIn || !userId || loading) {
    return {
      service: null,
      isReady: false,
      onNotification,
      onNotificationRead,
      onUnreadCountUpdate,
    }
  }

  return {
    service,
    isReady: isServiceReady,
    onNotification,
    onNotificationRead,
    onUnreadCountUpdate,
  }
}
